/* One source's harvest schedule, on the connection page's Schedule & automation tab.
 *
 * The design's card: an icon tile, the title and what it is for, and a state chip; then either an
 * invitation to set a schedule, the schedule as two facts (how it repeats, and when it next runs),
 * or the editor in place of those facts.
 *
 * **Five states, and the four that are not "on" carry the weight.** Not configured, disabled,
 * waiting for its first sweep and suspended are each a different sentence with a different next
 * step, and the whole reason `presenceOf` exists is that collapsing any pair of them makes a
 * working schedule and a broken one look alike. §9's absence-is-not-zero rule, on the platform's
 * own scheduler.
 */

import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { HarvestSchedule } from '@/shared/api/generated/catalog';
import { ApiError } from '@/shared/api/http';
import { personName } from '@/shared/model/person';
import { Button } from '@/shared/ui/Button';

import {
  useConfigureHarvestSchedule,
  useHarvestSchedule,
  useRemoveHarvestSchedule,
  useSetHarvestScheduleEnabled,
} from '../api/scheduling';
import { ageOf, nextOccurrence, occurrenceLabel } from '../model/occurrence';
import {
  TIMEZONES,
  WEEKDAY_ORDER,
  asOutcome,
  coversEveryDay,
  defaultRecurrence,
  presenceOf,
  recurrenceOf,
  recurrenceProblem,
  weekdayKey,
  withCadence,
  type Presence,
  type RecurrenceDraft,
} from '../model/presentation';

export interface HarvestScheduleCardProps {
  readonly connectionId: string;
  /** Whether this deployment's scheduling runtime is durable.
   *
   * `false` adds the durability caveat: the schedule stores and fires, and a restart of this
   * replica loses the in-flight timer. Passed in rather than read here, because `/readiness` is one
   * request for the whole page and a card issuing its own would poll it per connection.
   */
  readonly durable: boolean;
  /** Whether the reader holds `catalog.harvest_schedule:configure`. `can` hides controls and
   * protects nothing — the route refuses regardless — and a refused write also hides them, for a
   * deployment whose grants the client could not read. */
  readonly mayConfigure?: boolean;
}

const CHIP_TONE: Record<Exclude<Presence['kind'], 'unconfigured'>, string> = {
  armed: 'ok',
  unprimed: 'ok',
  disabled: 'neutral',
  suspended: 'warn',
};

function CalendarGlyph(): ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="3.5"
        y="5"
        width="17"
        height="15"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M3.5 10h17M8 3v4M16 3v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function HarvestScheduleCard({
  connectionId,
  durable,
  mayConfigure = true,
}: HarvestScheduleCardProps): ReactElement {
  const { t } = useTranslation();
  const schedule = useHarvestSchedule(connectionId);
  const configure = useConfigureHarvestSchedule();
  const setEnabled = useSetHarvestScheduleEnabled();
  const removal = useRemoveHarvestSchedule();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RecurrenceDraft>(defaultRecurrence());
  const titleId = useId();
  const repeatsId = useId();
  const daysId = useId();

  const frame = (chip: ReactNode, body: ReactNode): ReactElement => (
    <article className="lq-ac" aria-labelledby={titleId}>
      <header className="lq-ac__head">
        <span className="lq-ac__icon" data-tone="blue">
          <CalendarGlyph />
        </span>
        <div className="lq-ac__heading">
          <h2 className="lq-ac__title" id={titleId}>
            {t('scheduling.harvest.title')}
          </h2>
          <p className="lq-ac__sub">{t('scheduling.harvest.subtitle')}</p>
        </div>
        {chip}
      </header>
      {body}
    </article>
  );

  /* **A failed read is reported, never rendered as "not configured".** Those are a claim about this
     source and a failure of the request, and §9 records the same conflation shipping three times. A
     403 is its own sentence, because it sends a reader to a different person. */
  if (schedule.isError) {
    const forbidden = schedule.error instanceof ApiError && schedule.error.status === 403;
    return frame(
      null,
      <p className="lq-ac__text">
        {forbidden
          ? t('scheduling.harvest.forbidden')
          : t('scheduling.harvest.readFailed')}
      </p>,
    );
  }

  if (!schedule.isSuccess) {
    return frame(null, <p className="lq-ac__muted">{t('scheduling.loading')}</p>);
  }

  const stored: HarvestSchedule = schedule.data;
  const presence = presenceOf(stored);
  const outcome = asOutcome(stored.last_outcome);
  const now = new Date();

  /* **Reached by a refusal as well as by the grant.** There is no grant list on the wire for every
     deployment, so a write that comes back 403 hides the controls too — a button known to be
     refused invites a second press. */
  const refusedWrite = [configure.error, setEnabled.error, removal.error].some(
    (error) => error instanceof ApiError && error.status === 403,
  );
  const canChange = mayConfigure && !refusedWrite;

  const openEditor = (): void => {
    setDraft(recurrenceOf(stored));
    configure.reset();
    setEditing(true);
  };
  const closeEditor = (): void => {
    setEditing(false);
    configure.reset();
  };

  const needsAdmin = canChange ? null : (
    <p className="lq-ac__muted">{t('scheduling.harvest.needsAdmin')}</p>
  );

  /* ------------------------------------------------------------------ the editor, in place */

  if (editing && canChange) {
    const problem = recurrenceProblem(draft);
    const preview = problem === null ? nextOccurrence(draft, now) : null;
    /* The refusal the server sent, verbatim: a 422 names the timezone it refused and a 409 says the
       connection is out of service, both more specific than anything composed from a status. */
    const refusal =
      configure.error instanceof ApiError
        ? configure.error.message
        : configure.isError
          ? t('scheduling.form.saveFailed')
          : null;
    const toggleDay = (day: number): void => {
      setDraft({
        ...draft,
        weekdays: draft.weekdays.includes(day)
          ? draft.weekdays.filter((each) => each !== day)
          : [...draft.weekdays, day].sort((a, b) => a - b),
      });
    };

    return frame(
      null,
      <div className="lq-ac__form">
        <div className="lq-ac__fields">
          <div className="lq-ac__field">
            <span id={repeatsId}>{t('scheduling.harvest.repeats')}</span>
            <div className="lq-ac__seg" role="group" aria-labelledby={repeatsId}>
              {(['daily', 'weekly'] as const).map((cadence) => (
                <button
                  key={cadence}
                  type="button"
                  className="lq-ac__segOption"
                  aria-pressed={draft.cadence === cadence}
                  onClick={() => {
                    setDraft(withCadence(draft, cadence));
                  }}
                >
                  {t(`scheduling.cadence.${cadence}`)}
                </button>
              ))}
            </div>
          </div>
          <label className="lq-ac__field">
            {t('scheduling.harvest.timeLabel')}
            <input
              className="lq-input lq-mono lq-ac__time"
              value={draft.localTime}
              inputMode="numeric"
              placeholder="02:00"
              aria-invalid={problem === 'scheduling.form.invalid.time'}
              onChange={(event) => {
                setDraft({ ...draft, localTime: event.target.value });
              }}
            />
          </label>
          <label className="lq-ac__field">
            {t('scheduling.form.timezone')}
            {/* A list rather than free text, and that is the validation: an offset cannot express
                a daylight-saving transition, so the control must not be able to produce one. */}
            <select
              className="lq-input lq-ac__zone"
              value={draft.timezone}
              title={t('scheduling.form.timezoneHint')}
              onChange={(event) => {
                setDraft({ ...draft, timezone: event.target.value });
              }}
            >
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </label>
        </div>

        {draft.cadence === 'weekly' ? (
          <div className="lq-ac__field">
            <span id={daysId}>{t('scheduling.harvest.on')}</span>
            <div className="lq-ac__days" role="group" aria-labelledby={daysId}>
              {WEEKDAY_ORDER.map((day) => (
                <button
                  key={day}
                  type="button"
                  className="lq-ac__day"
                  aria-pressed={draft.weekdays.includes(day)}
                  onClick={() => {
                    toggleDay(day);
                  }}
                >
                  {t(weekdayKey(day))}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {preview !== null ? (
          <p className="lq-ac__preview">
            <span className="lq-ac__previewDot" aria-hidden="true" />
            {t('scheduling.harvest.preview', { when: preview, zone: draft.timezone })}
          </p>
        ) : null}
        {problem !== null ? (
          <p className="lq-ac__error" role="alert">
            {t(problem)}
          </p>
        ) : null}
        {refusal !== null ? (
          <p className="lq-ac__error" role="alert">
            {refusal}
          </p>
        ) : null}

        <div className="lq-ac__actions">
          <Button onClick={closeEditor}>{t('scheduling.form.cancel')}</Button>
          <Button
            tone="primary"
            disabled={problem !== null}
            busy={configure.isPending}
            onClick={() => {
              configure.mutate(
                {
                  connectionId,
                  body: {
                    /* **Editing keeps whatever it was; setting a new schedule turns it on.** A
                       reader adjusting a time has not decided to start or stop harvesting — that
                       has its own control — while somebody pressing "Set a schedule" has. */
                    enabled: stored.configured ? stored.enabled : true,
                    cadence: draft.cadence,
                    local_time: draft.localTime.trim(),
                    timezone: draft.timezone,
                    weekdays: [...draft.weekdays],
                  },
                },
                {
                  onSuccess: () => {
                    setEditing(false);
                  },
                },
              );
            }}
          >
            {t('scheduling.form.save')}
          </Button>
        </div>
      </div>,
    );
  }

  /* ------------------------------------------------------------------ nobody has set one */

  if (presence.kind === 'unconfigured') {
    return frame(
      null,
      <>
        <div className="lq-ac__empty">
          <p className="lq-ac__text">{t('scheduling.harvest.notScheduled')}</p>
          {canChange ? (
            <Button tone="primary" onClick={openEditor}>
              {t('scheduling.harvest.setSchedule')}
            </Button>
          ) : null}
        </div>
        {needsAdmin}
      </>,
    );
  }

  /* ------------------------------------------------------------------ the schedule, as two facts */

  const repeats =
    /* A weekly schedule covering every day *is* a daily one, and seven abbreviations is the
       longer way of saying so. */
    stored.cadence === 'weekly' && !coversEveryDay(stored.weekdays)
      ? t('scheduling.harvest.weekly', {
          days: (stored.weekdays ?? []).map((day) => t(weekdayKey(day))).join(', '),
          time: stored.local_time ?? '—',
        })
      : t('scheduling.harvest.daily', { time: stored.local_time ?? '—' });

  /* Three readings for "next", and they are three different facts: off, waiting for the sweep, and
     a time. A disabled schedule has no next run *because it is off*, and rendering a time would
     promise a harvest that will not happen. */
  const next =
    presence.kind === 'disabled'
      ? t('scheduling.harvest.paused')
      : presence.kind === 'suspended'
        ? t('scheduling.state.suspendedUntil', {
            until: occurrenceLabel(presence.until, stored.timezone) ?? presence.until,
          })
        : presence.kind === 'unprimed'
          ? t('scheduling.nextRun.waiting')
          : (occurrenceLabel(stored.next_run_at, stored.timezone) ??
            stored.next_run_at ??
            '—');

  const age =
    stored.last_run_at === null || stored.last_run_at === undefined
      ? null
      : ageOf(stored.last_run_at, now);
  const last =
    age === null || outcome === null
      ? t('scheduling.harvest.lastNever')
      : t('scheduling.harvest.last', {
          when:
            age.kind === 'justNow'
              ? t('scheduling.ago.justNow')
              : t(`scheduling.ago.${age.kind}`, { count: age.count }),
          outcome: t(`scheduling.outcome.${outcome}`),
        });

  const chip = (
    <span className="lq-ac__chip" data-tone={CHIP_TONE[presence.kind]}>
      <span className="lq-ac__chipDot" aria-hidden="true" />
      {presence.kind === 'disabled'
        ? t('scheduling.state.disabled')
        : presence.kind === 'suspended'
          ? t('scheduling.state.suspended')
          : t('scheduling.state.enabled')}
    </span>
  );

  const actionError = setEnabled.error ?? removal.error;

  return frame(
    chip,
    <>
      <div className="lq-ac__tiles">
        <div className="lq-ac__tile">
          <div className="lq-ac__tileLabel">{t('scheduling.harvest.repeats')}</div>
          <div className="lq-ac__tileValue">{repeats}</div>
          {/* The zone travels with the time, always: `02:00` alone is the ambiguity this whole
              feature exists to remove. */}
          <div className="lq-ac__tileSub">{stored.timezone ?? '—'}</div>
        </div>
        <div
          className="lq-ac__tile"
          /* Occurrences are claimed by a sweep on an interval, so a run starts at or shortly after
             this time — said on hover, so the minute passing without a harvest is not read as a
             fault. */
          title={t('scheduling.nextRun.resolution')}
        >
          <div className="lq-ac__tileLabel">{t('scheduling.harvest.next')}</div>
          <div className="lq-ac__tileValue">{next}</div>
          <div
            className="lq-ac__tileSub"
            data-tone={stored.needs_attention === true ? 'danger' : undefined}
          >
            {last}
          </div>
        </div>
      </div>

      {/* `needs_attention` comes from the server, and only `refused` and `failed` set it — a
          deferral stays quiet, or the highlight trains a reader to ignore it. */}
      {stored.needs_attention === true ? (
        <p className="lq-ac__error" title={stored.last_detail ?? undefined}>
          {outcome === 'failed'
            ? t('scheduling.harvest.lastFailed')
            : t('scheduling.harvest.lastRefused')}
        </p>
      ) : null}
      {presence.kind === 'suspended' ? (
        <p className="lq-ac__warn">
          {t('scheduling.state.suspended.reason', { reason: presence.reason })}
        </p>
      ) : null}
      {!durable ? (
        <p className="lq-ac__warn">{t('scheduling.state.notDurable')}</p>
      ) : null}
      {actionError !== null && !refusedWrite ? (
        <p className="lq-ac__error" role="alert">
          {actionError instanceof ApiError
            ? actionError.message
            : t('scheduling.form.toggleFailed')}
        </p>
      ) : null}

      {canChange ? (
        <div className="lq-ac__actions">
          <Button size="compact" onClick={openEditor}>
            {t('scheduling.form.edit')}
          </Button>
          {/* Its own request, matching the API's split route: pausing must not restate the cadence,
              because restating it is how a cadence gets changed by accident in the same action. */}
          <Button
            size="compact"
            busy={setEnabled.isPending}
            onClick={() => {
              setEnabled.mutate({ id: connectionId, enabled: !stored.enabled });
            }}
          >
            {stored.enabled
              ? t('scheduling.harvest.pause')
              : t('scheduling.harvest.resume')}
          </Button>
          <Button
            size="compact"
            busy={removal.isPending}
            onClick={() => {
              removal.mutate(connectionId);
            }}
          >
            {t('scheduling.harvest.remove')}
          </Button>
          <span className="lq-ac__spacer" />
          {stored.configured_by !== undefined && stored.configured_by !== '' ? (
            <span className="lq-ac__by">
              {t('scheduling.harvest.setBy', { who: personName(stored.configured_by) })}
            </span>
          ) : null}
        </div>
      ) : (
        needsAdmin
      )}
    </>,
  );
}
